// External imports
import { jsPDF as JSPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment-timezone';
// Internal imports
import { displayPrint } from '~/app/webHelpers.js';
// Image imports
import downloadIcon from '~/images/icons/downloadIcon.svg';

/**
 * Shared "print this list" support for the form viewer modals (Preparer Notes,
 * Event Log, Document Archive, K-1 Manager).
 *
 * Desktop gets this for free: every dialog declares a `PRINTDLG PRTLV:<ctrl>`
 * directive and F8 runs a generic engine (1040/scr/print.scr) that walks the
 * dialog's controls and prints the named listview. CLO has no equivalent, so
 * each modal instead hands its already-loaded rows to `printList` below.
 *
 * Output follows the existing CLO print precedent (Payments):
 * build a PDF client side, then hand it to `displayPrint` which opens the
 * viewer popup with download + close controls.
 */

const MARGIN = 12; // mm
const HEADER_TOP = 14;
const META_TOP = HEADER_TOP + 6;
const META_LINE_HEIGHT = 4; // mm at 9pt
const META_TABLE_GAP = 4;
const TABLE_TOP = 30; // floor for the table start; the meta block can push it down

const COLOR_TEXT = [26, 22, 60]; // #1A163C - CLO body text
const COLOR_MUTED = [110, 110, 125];
const COLOR_HEAD_BG = [240, 243, 248];

/**
 * Formats a timestamp for the printed page. Accepts anything moment can parse.
 *
 * @param {string|Date} value
 * @param {boolean} isUTC true when the source value is UTC and needs converting
 * @returns {string} formatted timestamp, or '' when the value is unusable
 */
export const formatPrintTimestamp = (value, isUTC = false) => {
  if (!value) return '';
  const parsed = isUTC ? moment.utc(value).local() : moment(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm:ss A') : '';
};

/**
 * Masks all but the last four digits of an SSN/EIN, preserving whatever
 * punctuation the value already carries, so '123-45-6789' prints as
 * 'XXX-XX-6789' and '12-3456789' as 'XX-XXX6789' - the same shapes
 * `formatMaskSSNEIN` produces elsewhere in the app.
 *
 * Printed listings always mask, regardless of the office's on-screen mask
 * preference: a printed page leaves the office. Desktop does the same on its
 * one hand-built print path (popup/poppayment.scr :PAYPRINT).
 *
 *
 * @param {string} value
 * @returns {string} masked value, or the input unchanged when there is nothing to hide
 */
export const maskSSNEIN = value => {
  const raw = value === null || value === undefined ? '' : String(value);
  const digitCount = (raw.match(/\d/g) || []).length;

  if (digitCount <= 4) return raw;

  let toMask = digitCount - 4;
  return raw.replace(/\d/g, digit => (toMask-- > 0 ? 'X' : digit));
};

/**
 * Wraps the meta block to the page width using the same font it is drawn in, so the
 * caller can measure how far down the table has to start. Measuring under a
 * different font would under-count the lines and let the header overprint the table.
 *
 * @param {object} doc jsPDF instance
 * @param {string[]} metaLines
 * @returns {string[]} wrapped lines
 */
const wrapMetaLines = (doc, metaLines) => {
  doc.setFont('helvetica', 'normal').setFontSize(9);
  return doc.splitTextToSize(metaLines.join('    '), doc.internal.pageSize.getWidth() - MARGIN * 2);
};

/**
 * Turns a meta object into the "Label: value" strings shown under the title.
 * Entries with no value are dropped so business returns don't print an empty
 * "Taxpayer:" line, and vice versa.
 *
 * @param {object} meta
 * @returns {string[]}
 */
const buildMetaLines = meta => {
  const { clientName, ssnein, taxYear, returnID, preparer } = meta;

  return [
    clientName && `Client: ${clientName}`,
    ssnein && `SSN/EIN: ${maskSSNEIN(ssnein)}`,
    taxYear && `Tax Year: ${taxYear}`,
    returnID && `Return ID: ${returnID}`,
    preparer && `Printed By: ${preparer}`,
    `Printed: ${formatPrintTimestamp(new Date())}`,
  ].filter(Boolean);
};

/**
 * Draws the title / meta block and the page footer. Runs per page so multi-page
 * listings stay identifiable if the pages get separated.
 *
 * @param {object} doc jsPDF instance
 * @param {string} title
 * @param {string[]} wrappedMeta pre-wrapped meta lines, see wrapMetaLines
 */
const drawPageFurniture = (doc, title, wrappedMeta) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc
    .setFont('helvetica', 'bold')
    .setFontSize(14)
    .setTextColor(...COLOR_TEXT);
  doc.text(title, MARGIN, HEADER_TOP);

  doc
    .setFont('helvetica', 'normal')
    .setFontSize(9)
    .setTextColor(...COLOR_MUTED);
  doc.text(wrappedMeta, MARGIN, META_TOP);

  const page = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.text(`Page ${page}`, pageWidth - MARGIN, pageHeight - 7, { align: 'right' });
  doc.setTextColor(...COLOR_TEXT);
};

/**
 * Builds and returns a printable PDF listing without opening the viewer -
 * `printList` below is the function with the side effect.
 *
 * Cell values are printed as they appear on screen - this mirrors desktop,
 * where the F8 engine prints the listview verbatim. Callers own the formatting
 * so the printed page matches what the preparer is looking at, with one
 * exception: SSN/EINs are always masked (see `maskSSNEIN`), whether or not the
 * office masks them on screen.
 *
 * @param {object}   config
 * @param {string}   config.title       heading, e.g. 'Preparer Notes'
 * @param {string[]} config.columns     column headers
 * @param {Array[]}  config.rows        array of row arrays, aligned to columns
 * @param {object}   [config.meta]      { clientName, ssnein, taxYear, returnID, preparer }
 * @param {object}   [config.columnStyles] per-index jspdf-autotable column styles
 * @param {string}   [config.emptyText] shown when there are no rows
 * @param {string}   [config.filename]  download filename (no spaces - it is not quoted downstream)
 */
export const buildListDoc = ({
  title,
  columns,
  rows,
  meta = {},
  columnStyles = {},
  emptyText = 'No records',
}) => {
  // Landscape across the board: every one of these listings carries at least one
  // free-text column (notes, details, descriptions) that needs the extra width.
  const doc = new JSPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const wrappedMeta = wrapMetaLines(doc, buildMetaLines(meta));
  const tableTop = Math.max(
    TABLE_TOP,
    META_TOP + wrappedMeta.length * META_LINE_HEIGHT + META_TABLE_GAP,
  );

  const body = rows?.length
    ? rows.map(row => row.map(cell => (cell === null || cell === undefined ? '' : String(cell))))
    : [[{ content: emptyText, colSpan: columns.length, styles: { halign: 'center' } }]];

  autoTable(doc, {
    head: [columns],
    body,
    startY: tableTop,
    margin: { top: tableTop, left: MARGIN, right: MARGIN, bottom: 14 },
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 2,
      overflow: 'linebreak',
      textColor: COLOR_TEXT,
    },
    headStyles: {
      fontStyle: 'bold',
      fillColor: COLOR_HEAD_BG,
      textColor: COLOR_TEXT,
    },
    alternateRowStyles: { fillColor: [250, 250, 252] },
    columnStyles,
    didDrawPage: () => drawPageFurniture(doc, title, wrappedMeta),
  });

  return doc;
};

/**
 * Builds the listing and opens it in the CLO print viewer popup.
 *
 * @param {object} config see `buildListDoc`
 * @param {string} [config.filename] download filename - keep it free of spaces,
 *                 `displayPrint` interpolates it into an unquoted download attribute
 */
export const printList = ({ filename = 'listing.pdf', ...config }) => {
  const doc = buildListDoc(config);

  const pdfURI = doc.output('datauristring');
  const pdfString = pdfURI.replace('data:application/pdf;filename=generated.pdf;base64,', '');

  displayPrint(pdfString, downloadIcon, 'application/pdf', config.title, 'Download', filename);
};
